import { createSlice } from '@reduxjs/toolkit';
import {
    deleteAnalysis,
    deletePatient,
    deleteStudy,
    fetchDoctorsLink,
    fetchPatients,
    fetchPatientsByName,
    fetchPatientStudiesWithImagesAndAnalyses,
    requestStudyAnalysis,
    createPatient as createPatientService,
} from '../../services/diagnocat/diagnocat.service';
import {
    getAllRadiologists,
    updateAppointmentLink,
} from '../../services/diagnocat/diagnocat-ctdent.service';
import {
    getAppointmentByPatientId,
    updateAppointment,
} from '../../services/doctors/appointment.service';
import { IDLE, PENDING } from '../../assets/conf/configurations';
import { addLoading, removeLoading } from '../uiSlice';

const diagnocatInitialState = {
    patients: [],
    //TODO: convert 'loading' into object, with loading value for each action
    loading: IDLE,
    patientsLoading: IDLE,
    studiesLoading: IDLE,
    error: {},
    selectedPatient: {},
    selectedPatientStudies: [],
    selectedPatientAppointment: null,
    querySearch: '',
    pdfTypes: [],
    radiologists: [],
    patientsCount: 0,
    page: 0,
};

export const diagnocatSlice = createSlice({
    name: 'diagnocat',
    initialState: diagnocatInitialState,
    reducers: {
        // standard reducer logic, with auto-generated action types per reducer
        fetchAllPatientsLoading(state, action) {
            if (state.patientsLoading === IDLE) {
                state.patientsLoading = PENDING;
            }
        },
        fetchAllPatientsLoadingSucceed(state, action) {
            state.patientsLoading = IDLE;
            state.patients = action.payload;
        },
        fetchAllPatientsLoadingFailed(state, action) {
            if (state.patientsLoading === PENDING) {
                state.patientsLoading = IDLE;
                state.error = action.payload;
            }
        },

        createPatientLoading(state, action) {
            if (state.loading === IDLE) {
                state.loading = PENDING;
            }
        },
        createPatientSucceed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;

                const newPatient = {
                    id: action.payload.diagnocat_uid,
                    patientId: action.payload.id.toString(),
                    lastName: action.payload.last_name,
                    firstName: action.payload.first_name,
                    dateOfBirth: action.payload.dob,
                    gender: action.payload.gender,
                };
                state.patients = state.patients.concat(newPatient);
                state.selectedPatient = newPatient;
            }
        },
        createPatientFailed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;
                state.error = action.payload;
            }
        },

        deletePatientLoading(state, action) {
            if (state.loading === IDLE) {
                state.loading = PENDING;
            }
        },
        deletePatientSucceed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;
                state.patients = state.patients.filter((patient) => patient.id !== action.payload);
            }
        },
        deletePatientFailed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;
                state.error = action.payload;
            }
        },

        deleteStudyLoading(state, action) {
            if (state.loading === IDLE) {
                state.loading = PENDING;
            }
        },
        deleteStudySucceed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;
                state.selectedPatientStudies = state.selectedPatientStudies.filter(
                    (study) => study.uid !== action.payload
                );
            }
        },
        deleteStudyFailed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;
                state.error = action.payload;
            }
        },

        deleteAnalysisLoading(state, action) {
            if (state.studiesLoading === IDLE) {
                state.studiesLoading = PENDING;
            }
        },
        deleteAnalysisSucceed(state, action) {
            if (state.studiesLoading === PENDING) {
                return {
                    ...state,
                    studiesLoading: IDLE,
                    selectedPatientStudies: state.selectedPatientStudies.map((study) =>
                        study.uid === action.payload.studyUID
                            ? // transform the one with a matching study id
                              {
                                  ...study,
                                  analyses: study.analyses.filter(
                                      (analysis) => analysis.uid !== action.payload.analysisUID
                                  ),
                              }
                            : // otherwise return original study
                              study
                    ),
                };
            }
        },
        deleteAnalysisFailed(state, action) {
            if (state.studiesLoading === PENDING) {
                state.studiesLoading = IDLE;
                state.error = action.payload;
            }
        },

        loadPatientStudiesWithImagesAndAnalysesLoading(state, action) {
            if (state.studiesLoading === IDLE) {
                state.studiesLoading = PENDING;
            }
        },
        loadPatientStudiesWithImagesAndAnalysesSucceed(state, action) {
            state.selectedPatientStudies = action.payload;
            state.studiesLoading = IDLE;
        },

        loadPatientStudiesWithImagesAndAnalysesFailed(state, action) {
            if (state.studiesLoading === PENDING) {
                state.studiesLoading = IDLE;
                state.error = action.payload;
            }
        },

        fetchTypesLoading(state, action) {
            if (state.loading === IDLE) {
                state.loading = PENDING;
            }
        },
        fetchTypesSucceed(state, action) {
            state.pdfTypes = action.payload;
            state.loading = IDLE;
        },

        fetchTypesFailed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;
                state.error = action.payload;
            }
        },

        requestAnalysisSucceed(state, action) {
            return {
                ...state,
                selectedPatientStudies: state.selectedPatientStudies.map((study) =>
                    study.uid === action.payload.study_uid
                        ? // transform the one with a matching study id
                          { ...study, analyses: study.analyses.concat(action.payload) }
                        : // otherwise return original study
                          study
                ),
            };
        },

        setSelectedPatient(state, action) {
            const patient = state.patients.find((patient) => patient.patientId === action.payload);
            state.selectedPatient = patient;
        },

        clearSelectedPatient(state, action) {
            state.selectedPatient = null;
            state.selectedPatientStudies = null;
            state.selectedPatientAppointment = null;
        },

        createDoctorsLinkLoading(state, action) {
            if (state.loading === IDLE) {
                state.loading = PENDING;
            }
        },
        createDoctorsLinkSucceed(state, action) {
            state.selectedPatientAppointment = {
                ...state.selectedPatientAppointment,
                diagnocat_link: action.payload,
            };
            state.loading = IDLE;
        },
        createDoctorsLinkFailed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;
                state.error = action.payload;
            }
        },

        loadAppointmentByPatientIdLoading(state, action) {
            if (state.loading === IDLE) {
                state.loading = PENDING;
            }
        },
        loadAppointmentByPatientIdSucceed(state, action) {
            state.selectedPatientAppointment = action.payload;
            state.loading = IDLE;
        },
        loadAppointmentByPatientIdFailed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;
                state.error = action.payload;
            }
        },

        updateAppointmentStatusLoading(state, action) {
            if (state.loading === IDLE) {
                state.loading = PENDING;
            }
        },
        updateAppointmentStatusSucceed(state, action) {
            state.selectedPatientAppointment = {
                ...state.selectedPatientAppointment,
                diagnocat_status: action.payload,
            };
            state.loading = IDLE;
        },
        updateAppointmentStatusFailed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;
                state.error = action.payload;
            }
        },

        fetchRadiologistsLoading(state, action) {
            if (state.loading === IDLE) {
                state.loading = PENDING;
            }
        },
        fetchRadiologistsSucceed(state, action) {
            state.radiologists = action.payload;
            state.loading = IDLE;
        },
        fetchRadiologistsFailed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;
                state.error = action.payload;
            }
        },

        fetchPatientsCountLoading(state, action) {
            if (state.loading === IDLE) {
                state.loading = PENDING;
            }
        },
        fetchPatientsCountSucceed(state, action) {
            state.patientsCount = action.payload;
            state.loading = IDLE;
        },
        fetchPatientsCountFailed(state, action) {
            if (state.loading === PENDING) {
                state.loading = IDLE;
                state.error = action.payload;
            }
        },
        setQuerySearch(state, action){
            state.querySearch = action.payload;
        },
        setPage(state, action){
            state.page = action.payload;
        }
    },
});

// Destructure and export the plain action creators
export const {
    fetchAllPatientsLoading,
    fetchAllPatientsLoadingSucceed,
    fetchAllPatientsLoadingFailed,
    createPatientLoading,
    createPatientSucceed,
    createPatientFailed,
    deletePatientLoading,
    deletePatientSucceed,
    deletePatientFailed,
    deleteStudyLoading,
    deleteStudySucceed,
    deleteStudyFailed,
    deleteAnalysisLoading,
    deleteAnalysisSucceed,
    deleteAnalysisFailed,
    loadPatientStudiesWithImagesAndAnalysesLoading,
    loadPatientStudiesWithImagesAndAnalysesSucceed,
    loadPatientStudiesWithImagesAndAnalysesFailed,
    createDoctorsLinkLoading,
    createDoctorsLinkSucceed,
    createDoctorsLinkFailed,
    clearSelectedPatient,
    setSelectedPatient,
    requestAnalysisSucceed,
    fetchTypesLoading,
    fetchTypesSucceed,
    fetchTypesFailed,
    loadAppointmentByPatientIdLoading,
    loadAppointmentByPatientIdSucceed,
    loadAppointmentByPatientIdFailed,
    updateAppointmentStatusLoading,
    updateAppointmentStatusSucceed,
    updateAppointmentStatusFailed,
    fetchRadiologistsLoading,
    fetchRadiologistsSucceed,
    fetchRadiologistsFailed,
    fetchPatientsCountLoading,
    fetchPatientsCountSucceed,
    fetchPatientsCountFailed,
    setQuerySearch,
    setPage,
} = diagnocatSlice.actions;

/**
 * REDUX THUNKS
 * Thunks dispatch action creators
 * */
export const fetchAllPatients = () => async (dispatch) => {
    dispatch(fetchAllPatientsLoading());
    try {
        let patients = await fetchPatients();
        dispatch(fetchAllPatientsLoadingSucceed(patients));
    } catch (error) {
        dispatch(fetchAllPatientsLoadingFailed(error));
    }
};

export const fetchSearchPatients = (name) => async (dispatch) => {
    dispatch(fetchAllPatientsLoading());
    try {
        dispatch(addLoading())
        let patients = await fetchPatientsByName(name);
        dispatch(removeLoading())
        dispatch(fetchAllPatientsLoadingSucceed(patients));
    } catch (error) {
        dispatch(fetchAllPatientsLoadingFailed(error));
    }
};

export const createPatient = (data) => async (dispatch) => {
    dispatch(addLoading());
    try {


        const response = await createPatientService(data);

        dispatch(setSelectedPatient(response.id));
        dispatch(createPatientSucceed(response.data.data));
    } catch (error) {
        dispatch(createPatientFailed(error));
    }finally{
        dispatch(removeLoading())
    }
};

export const deletePatientState = (patientId) => async (dispatch) => {
    dispatch(deletePatientLoading());
    try {
        const response = await deletePatient(patientId);
        dispatch(deletePatientSucceed(patientId));
    } catch (error) {
        dispatch(deletePatientFailed(error));
    }
};

export const deleteStudyState = (studyId) => async (dispatch) => {
    dispatch(deleteStudyLoading());
    try {
        const response = await deleteStudy(studyId);
        // dispatch(deleteStudySucceed(response.data));
        dispatch(deleteStudySucceed(studyId));
    } catch (error) {
        dispatch(deleteStudyFailed(error));
    }
};

export const deleteAnalysisState = (studyUID, analysisUID) => async (dispatch) => {
    dispatch(deleteAnalysisLoading());
    try {
        const response = await deleteAnalysis(analysisUID);
        dispatch(deleteAnalysisSucceed({ studyUID, analysisUID }));
    } catch (error) {
        dispatch(deleteAnalysisFailed(error));
    }
};

export const sendLinkToDoctor = (patientUID, doctorEmail, appointmentId) => async (dispatch) => {
    dispatch(createDoctorsLinkLoading());
    try {
        //create Doctors Link (Diagnocat API)
        const diagnocatResponse = await fetchDoctorsLink(patientUID, doctorEmail);
        //save to ct-dent db
        const response1 = await updateAppointmentLink(appointmentId, diagnocatResponse.link);
        dispatch(createDoctorsLinkSucceed(diagnocatResponse.link));
    } catch (error) {
        dispatch(createDoctorsLinkFailed(error));
    }
};

/**
 * loads selected patient and studies(including images and analyses)
 */
export const loadPatientStudiesWithImagesAndAnalyses = (patientId) => async (dispatch) => {
    dispatch(loadPatientStudiesWithImagesAndAnalysesLoading());
    try {
        const patientStudiesWithImagesAndAnalyses = await fetchPatientStudiesWithImagesAndAnalyses(
            patientId
        );
        dispatch(
            loadPatientStudiesWithImagesAndAnalysesSucceed(patientStudiesWithImagesAndAnalyses)
        );
    } catch (error) {
        dispatch(loadPatientStudiesWithImagesAndAnalysesFailed(error));
    }
};

export const loadAppointmentByPatientId = (patientId) => async (dispatch) => {
    dispatch(loadAppointmentByPatientIdLoading());
    try {
        const appointment = await getAppointmentByPatientId(patientId);
        dispatch(loadAppointmentByPatientIdSucceed(appointment));
    } catch (error) {
        dispatch(loadAppointmentByPatientIdFailed(error));
    }
};

export const requestAnalysis = (studyId, analysisRequest) => async (dispatch) => {
    const requestedAnalysis = await requestStudyAnalysis(studyId, analysisRequest);
    dispatch(requestAnalysisSucceed(requestedAnalysis, studyId));
};


export const updateAppointmentStatus = (appointmentId, fieldValue) => async (dispatch) => {
    dispatch(updateAppointmentStatusLoading());
    try {
        const res = await updateAppointment(appointmentId, 'diagnocat_status', fieldValue);
        dispatch(updateAppointmentStatusSucceed(res.data.value));
    } catch (error) {
        dispatch(updateAppointmentStatusFailed(error));
    }
};

export const fetchRadiologists = () => async (dispatch) => {
    dispatch(fetchRadiologistsLoading());
    try {
        const res = await getAllRadiologists();
        dispatch(fetchRadiologistsSucceed(res.data));
    } catch (error) {
        dispatch(fetchRadiologistsFailed(error));
    }
};


export const selectPatients = (state) => state.diagnocat.patients;
export const selectLoading = (state) => state.diagnocat.loading;
export const selectPatientsLoading = (state) => state.diagnocat.patientsLoading;
export const selectStudiesLoading = (state) => state.diagnocat.studiesLoading;
export const selectStudies = (state) => state.diagnocat.selectedPatientStudies;
export const selectPatient = (state) => state.diagnocat.selectedPatient;
export const selectAppointment = (state) => state.diagnocat.selectedPatientAppointment;
export const selectTypes = (state) => state.diagnocat.pdfTypes;
export const selectRadiologists = (state) => state.diagnocat.radiologists;
export const selectPatientsCount = (state) => state.diagnocat.patientsCount;
export const selectQuerySearch = (state) => state.diagnocat.querySearch;
export const selectPage = state => state.diagnocat.page;
export default diagnocatSlice.reducer;
